import { useState, useEffect } from 'react';
import { useApp } from '../App';
import api from '../api';
import BackButton from '../components/BackButton';
import { useI18n } from '../i18n/index.jsx';

const COMPANY_ROLES = [
  { id: 'director', name: 'Директор', icon: '👔', permissions: ['all'] },
  { id: 'accountant', name: 'Бухгалтер', icon: '📊', permissions: ['invoices', 'reports', 'accounting'] },
  { id: 'manager', name: 'Менеджер', icon: '💼', permissions: ['invoices', 'clients', 'contracts'] },
  { id: 'employee', name: 'Сотрудник', icon: '👤', permissions: ['view_own'] },
];

export default function CompanyRoles() {
  const { user } = useApp();
  const { t } = useI18n();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    email: '',
    role: 'employee',
  });

  useEffect(() => {
    loadMembers();
  }, []);

  async function loadMembers() {
    try {
      // Load from API or localStorage for demo
      const saved = localStorage.getItem('company_members');
      if (saved) {
        setMembers(JSON.parse(saved));
      } else {
        // Demo data
        setMembers([
          { id: 1, name: user?.first_name || 'Вы', email: user?.email || '', role: 'director', status: 'active' },
        ]);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }

  function inviteMember() {
    if (!form.email) {
      alert('Введите email');
      return;
    }
    
    const newMember = {
      id: Date.now(),
      name: form.email.split('@')[0],
      email: form.email,
      role: form.role,
      status: 'pending',
      invited_at: new Date().toISOString(),
    };
    
    const updated = [...members, newMember];
    setMembers(updated);
    localStorage.setItem('company_members', JSON.stringify(updated));
    setShowForm(false);
    setForm({ email: '', role: 'employee' });
  }

  function changeRole(memberId, newRole) {
    const updated = members.map(m => 
      m.id === memberId ? { ...m, role: newRole } : m
    );
    setMembers(updated);
    localStorage.setItem('company_members', JSON.stringify(updated));
  }

  function removeMember(memberId) {
    if (memberId === 1) {
      alert('Нельзя удалить себя');
      return;
    }
    const updated = members.filter(m => m.id !== memberId);
    setMembers(updated);
    localStorage.setItem('company_members', JSON.stringify(updated));
  }

  function getRoleInfo(roleId) {
    return COMPANY_ROLES.find(r => r.id === roleId) || COMPANY_ROLES[3];
  }

  if (loading) return <div className="page"><BackButton /><div className="loader">{t('common.loading')}</div></div>;

  return (
    <div className="page">
      <BackButton />
      <h1>👥 Роли в фирме</h1>

      {/* Roles Legend */}
      <div style={{
        background: 'rgba(255,255,255,0.05)',
        borderRadius: 12,
        padding: 16,
        marginBottom: 16,
      }}>
        <div style={{ fontWeight: 'bold', marginBottom: 12 }}>📋 Доступные роли:</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8 }}>
          {COMPANY_ROLES.map(role => (
            <div key={role.id} style={{
              background: 'rgba(255,255,255,0.05)',
              borderRadius: 8,
              padding: 12,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                <span style={{ fontSize: 20 }}>{role.icon}</span>
                <span style={{ fontWeight: 'bold' }}>{role.name}</span>
              </div>
              <div style={{ fontSize: 11, opacity: 0.7 }}>
                {role.permissions.includes('all') ? 'Полный доступ' : role.permissions.join(', ')}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Add Button */}
      {!showForm && (
        <button
          className="btn btn-primary"
          style={{ width: '100%', marginBottom: 16 }}
          onClick={() => setShowForm(true)}
        >
          ➕ Пригласить сотрудника
        </button>
      )}

      {/* Invite Form */}
      {showForm && (
        <div style={{
          background: 'rgba(255,255,255,0.05)',
          borderRadius: 12,
          padding: 20,
          marginBottom: 16,
        }}>
          <h3 style={{ marginTop: 0 }}>Приглашение</h3>
          
          <div style={{ marginBottom: 12 }}>
            <label style={{ display: 'block', marginBottom: 4, opacity: 0.7 }}>Email *</label>
            <input
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              placeholder="colleague@email.com"
              style={{
                width: '100%', padding: 12, borderRadius: 8,
                border: '1px solid rgba(255,255,255,0.2)',
                background: 'rgba(255,255,255,0.05)', color: '#fff',
              }}
            />
          </div>

          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', marginBottom: 4, opacity: 0.7 }}>Роль</label>
            <select
              value={form.role}
              onChange={(e) => setForm({ ...form, role: e.target.value })}
              style={{
                width: '100%', padding: 12, borderRadius: 8,
                border: '1px solid rgba(255,255,255,0.2)',
                background: 'rgba(255,255,255,0.05)', color: '#fff',
              }}
            >
              {COMPANY_ROLES.map(r => (
                <option key={r.id} value={r.id}>{r.icon} {r.name}</option>
              ))}
            </select>
          </div>

          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-primary" style={{ flex: 1 }} onClick={inviteMember}>
              📧 Отправить приглашение
            </button>
            <button className="btn" style={{ background: 'rgba(255,255,255,0.1)' }} onClick={() => setShowForm(false)}>
              Отмена
            </button>
          </div>
        </div>
      )}

      {/* Members List */}
      <h3>👥 Участники ({members.length})</h3>
      {members.map(member => {
        const roleInfo = getRoleInfo(member.role);
        return (
          <div key={member.id} style={{
            background: 'rgba(255,255,255,0.05)',
            borderRadius: 12,
            padding: 16,
            marginBottom: 8,
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: 8 }}>
              <div>
                <div style={{ fontWeight: 'bold' }}>{member.name}</div>
                <div style={{ fontSize: 12, opacity: 0.7 }}>{member.email}</div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {member.status === 'pending' && (
                  <span style={{
                    background: '#f59e0b',
                    padding: '2px 6px',
                    borderRadius: 4,
                    fontSize: 10,
                  }}>
                    Ожидает
                  </span>
                )}
                <span style={{
                  background: 'rgba(99,102,241,0.3)',
                  padding: '4px 8px',
                  borderRadius: 4,
                  fontSize: 12,
                }}>
                  {roleInfo.icon} {roleInfo.name}
                </span>
              </div>
            </div>

            {member.id !== 1 && (
              <div style={{ display: 'flex', gap: 8 }}>
                <select
                  value={member.role}
                  onChange={(e) => changeRole(member.id, e.target.value)}
                  style={{
                    flex: 1, padding: 8, borderRadius: 6,
                    background: 'rgba(255,255,255,0.1)', color: '#fff', border: 'none',
                  }}
                >
                  {COMPANY_ROLES.map(r => (
                    <option key={r.id} value={r.id}>{r.name}</option>
                  ))}
                </select>
                <button
                  className="btn btn-small"
                  style={{ background: 'rgba(239,68,68,0.3)' }}
                  onClick={() => removeMember(member.id)}
                >
                  🗑
                </button>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
